import { List, Map } from "immutable";
import createCachedSelector from "re-reselect";
import { createSelector } from "reselect";

import {
  MINDMAP_BOUNDARY_MIN_PADDING,
  MINDMAP_NODE_AVATAR_SIZE,
  MINDMAP_NODE_LEFT_SPACE_TO_ITEM_CARD,
  MINDMAP_NODE_MIN_WIDTH,
  MINDMAP_NODE_PADDING_Y,
  MINDMAP_NODE_RIGHT_SPACE_TO_ITEM_CARD
} from "../../constants";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { MINDMAP_VIEW } from "../../constants/identifiers";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { appStateSelectors } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { typeSelectors } from "../../modules/definition/types";
import { mindmapSelectors } from "../../modules/mindmaps";
import { relationshipSelectors } from "../../modules/relationships";
import { createFromJS, immutableRecords } from "../../types";
import workViewSelectors from "../workView/selectors";
import { connectorForRects } from "./utils";

const getMindmapNodeDisplayRecord = (
  nodeId,
  itemId,
  left,
  top,
  displayItemsMap,
  typesById,
  mindmapViewSettings,
  fontSize
) => {
  const SCALED_MIN_NODE_WIDTH = scalePixelsToFontSize(MINDMAP_NODE_MIN_WIDTH, fontSize);
  const SCALED_NODE_AVATAR_SIZE = scalePixelsToFontSize(MINDMAP_NODE_AVATAR_SIZE, fontSize);
  const SCALED_NODE_LEFT_SPACE = scalePixelsToFontSize(MINDMAP_NODE_LEFT_SPACE_TO_ITEM_CARD, fontSize);
  const SCALED_NODE_RIGHT_SPACE = scalePixelsToFontSize(MINDMAP_NODE_RIGHT_SPACE_TO_ITEM_CARD, fontSize);
  const SCALED_NODE_PADDING_Y = scalePixelsToFontSize(MINDMAP_NODE_PADDING_Y, fontSize);
  const SCALED_NODE_IDENTIFIER_HEIGHT = scalePixelsToFontSize(19, fontSize);

  const scaledLeft = scalePixelsToFontSize(left, fontSize);
  const scaledTop = scalePixelsToFontSize(top, fontSize);

  const obj = displayItemsMap.get(itemId);
  const objType = typesById.get(obj.type);
  const typeData = typeSelectors.getTypeValuesFromType(objType);

  const isAvatarType = typeSelectors.isAvatarType(objType);

  const identifierWidth = mindmapViewSettings.showIdentifier
    ? isAvatarType
      ? SCALED_NODE_AVATAR_SIZE
      : obj.identifierSize.width
    : 0;

  const identifierHeight = mindmapViewSettings.showIdentifier
    ? isAvatarType
      ? SCALED_NODE_AVATAR_SIZE
      : SCALED_NODE_IDENTIFIER_HEIGHT
    : 0;

  const width = Math.max(
    SCALED_MIN_NODE_WIDTH,
    obj.itemCardSize.width + SCALED_NODE_LEFT_SPACE + SCALED_NODE_RIGHT_SPACE + identifierWidth
  );
  const height = Math.max(obj.itemCardSize.height, identifierHeight) + 2 * SCALED_NODE_PADDING_Y;
  const rect = immutableRecords.FlatRectRecord({
    left: scaledLeft,
    top: scaledTop,
    width,
    height,
    right: scaledLeft + width,
    bottom: scaledTop + height
  });

  return immutableRecords.MindmapNodeDisplayRecord({
    id: nodeId,
    itemId: itemId,
    itemDisplay: obj,
    typeData,
    rect,
    nodeType: DragDropTypes.EVENT_NODE
  });
};

const getMindmapData = (
  nodes,
  filteredItemIds,
  displayItemsMap,
  typesById,
  referencesById,
  relationshipsById,
  mindmapViewSettings,
  hiddenReferenceTypes,
  fontSize
) => {
  const includedDisplayItems = Map(
    nodes.reduce((result, n) => {
      result[n.itemId] = displayItemsMap.get(n.itemId);
      return result;
    }, {})
  );
  // Keep the filtered out items but flag them
  const filteredDisplayItems = includedDisplayItems.map(i =>
    !filteredItemIds || filteredItemIds.contains(i.id) ? i : i.set("isFiltered", true)
  );
  let outputNodes = nodes
    .toList()
    .map(n =>
      getMindmapNodeDisplayRecord(
        n.id,
        n.itemId,
        n.left,
        n.top,
        filteredDisplayItems,
        typesById,
        mindmapViewSettings,
        fontSize
      )
    );
  const nodeIds = outputNodes.map(n => n.itemId).toList();
  const visibleReferenceTypes = referencesById
    .map(r => r.id)
    .toList()
    .filter(id => !hiddenReferenceTypes.contains(id));
  const relationships = relationshipSelectors.getRelationshipsBetweenItemsWithReferences(
    relationshipsById,
    nodeIds,
    visibleReferenceTypes
  );

  const isCollision = (r1, r2) =>
    ((r1.left < r2.right && r1.right > r2.left) || (r1.left > r2.right && r1.right < r2.left)) &&
    ((r1.top < r2.bottom && r1.bottom > r2.top) || (r1.top > r2.bottom && r1.bottom < r2.top));

  outputNodes = Map(outputNodes.map(n => [n.itemId, n]));

  const connectors = [];

  // TODO: Long term, we want to penalise other things like crossing over
  // other nodes where it can be avoided.
  relationships.forEach((rel, relId) => {
    const itemId = rel.object;
    const subjectId = rel.subject;
    const reference = referencesById.get(rel.reference);

    const oItem = outputNodes.get(itemId);
    const sItem = outputNodes.get(subjectId);
    const oRect = oItem.rect.toObject();
    const sRect = sItem.rect.toObject();
    const oIsFiltered = oItem.itemDisplay.isFiltered;
    const sIsFiltered = sItem.itemDisplay.isFiltered;

    const c = connectorForRects(sRect, oRect);
    const overlap = isCollision(sRect, oRect);

    let label = null;
    let labelCenter = null;
    if (mindmapViewSettings.showConnectorLabels) {
      label = reference.label;
      labelCenter = immutableRecords.PointRecord({
        x: (c.first().x + c.last().x) / 2,
        y: (c.first().y + c.last().y) / 2
      });
    }

    connectors.push(
      immutableRecords.MindmapConnectorDisplayRecord({
        id: relId,
        color: reference.color,
        points: c,
        labelCenter,
        label,
        lineStyle: reference.lineStyle,
        subjectId,
        itemId,
        overlap,
        isFiltered: oIsFiltered || sIsFiltered
      })
    );
  });

  const connectorList = List(connectors);
  return immutableRecords.MindmapDisplayRecord({
    nodes: outputNodes.toList(),
    connectors: connectorList
  });
};

const getMindmapDataForViewWithIds = createCachedSelector(
  // fetch parameters for last calculate function
  (state, viewId) => mindmapSelectors.getMindmapNodes(state, appStateSelectors.getViewMindmapId(state, viewId)),
  (_state, viewId, ids) => ids,
  state => workViewSelectors.getDisplayItemsForViewType(state, MINDMAP_VIEW),
  state => definitionSelectors.getTypesById(state),
  state => definitionSelectors.getReferencesById(state),
  state => state.getIn(["data", "relationships", "byId"]),
  state => definitionSelectors.getMindmapViewSettings(state),
  (state, viewId) =>
    mindmapSelectors.getMindmapHiddenReferenceTypes(state, appStateSelectors.getViewMindmapId(state, viewId)),
  appStateSelectors.getFontSize,
  // appStateSelectors.getViewMindmapId,
  // and calculate
  getMindmapData
)(workViewSelectors.getViewId);

const getMindmapDataForView = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getItemIdsMatchingFilterForView,
  getMindmapDataForViewWithIds
)(workViewSelectors.getViewId);

const getSelectedMindmapDataForView = createCachedSelector(
  state => state,
  (_state, viewId) => viewId,
  workViewSelectors.getSelectedItemIdsMatchingFilterForView,
  getMindmapDataForViewWithIds
)(workViewSelectors.getViewId);

const getMindmapViewDataForItem = createSelector(
  // fetch parameters for last calculate function
  (_state, itemId) => itemId,
  state => workViewSelectors.getDisplayItemsForViewType(state, MINDMAP_VIEW),
  state => definitionSelectors.getTypesById(state),
  state => definitionSelectors.getMindmapViewSettings(state),
  appStateSelectors.getFontSize,
  // appStateSelectors.getViewMindmapId,
  // and calculate
  (itemId, displayItemsMap, typesById, mindmapViewSettings, fontSize) => {
    const nodeId = `tempNode-${itemId}`;
    const left = 0;
    const top = 0;
    return getMindmapNodeDisplayRecord(
      nodeId,
      itemId,
      left,
      top,
      displayItemsMap,
      typesById,
      mindmapViewSettings,
      fontSize
    );
  }
);

const calculateMindmapViewData = (data, _fontSize, openViewsById, workViewId) => {
  const nodes = data.nodes;
  let left = nodes.reduce((val, n) => (val === null ? n.rect.left : Math.min(val, n.rect.left)), null);
  let top = nodes.reduce((val, n) => (val === null ? n.rect.top : Math.min(val, n.rect.top)), null);
  let right = nodes.reduce((val, n) => (val === null ? n.rect.right : Math.max(val, n.rect.right)), null);
  let bottom = nodes.reduce((val, n) => (val === null ? n.rect.bottom : Math.max(val, n.rect.bottom)), null);

  const workview = openViewsById.get(workViewId);
  const size = workview.size;
  let widthPadding = Math.max(Math.ceil(size.width / 2), MINDMAP_BOUNDARY_MIN_PADDING);
  let heightPadding = Math.max(Math.ceil(size.height / 2), MINDMAP_BOUNDARY_MIN_PADDING);

  return createFromJS.MindmapDisplayRecord({
    nodes: data.nodes,
    connectors: data.connectors,
    rect: {
      left: left - widthPadding,
      right: right + widthPadding,
      top: top - heightPadding,
      bottom: bottom + heightPadding
    }
  });
};

// Merges the event layout with the current selection.
// Receives: state, viewId
const getMindmapViewData = createCachedSelector(
  // fetch parameters for last calculate function
  getMindmapDataForView,
  appStateSelectors.getFontSize,
  appStateSelectors.getOpenViewsById,
  (_state, workViewId) => workViewId,
  // Calculate result
  calculateMindmapViewData
)(workViewSelectors.getViewId);

const getMindmapViewSelectedData = createCachedSelector(
  getSelectedMindmapDataForView,
  appStateSelectors.getFontSize,
  appStateSelectors.getOpenViewsById,
  (_state, workViewId) => workViewId,
  calculateMindmapViewData
)(workViewSelectors.getViewId);

const getMindmapViewSortedItemIds = createCachedSelector(
  // fetch parameters for last calculate function
  getMindmapDataForView,
  // Calculate result
  data => {
    return data.nodes.map(n => n.itemId);
  }
)(workViewSelectors.getViewId);

const getMindmapViewDefaultType = state => definitionSelectors.getDefaultSequenceType(state);

export default {
  getMindmapViewData,
  getMindmapViewSelectedData,
  getMindmapViewSortedItemIds,
  getMindmapViewDataForItem,
  getMindmapViewDefaultType
};
